import { AUTH_KEY } from '../context/authContext';
import { AuthObject } from '../types/auth';
import LocalStorageService from './localstorage.service';

enum RequestType {
    GET = 'GET',
    POST = 'POST',
    PATCH = 'PATCH',
    DELETE = 'DELETE',
    PUT = 'PUT'
}

type MapHeader = Record<string, string>;

class APIRequest {
    type: RequestType = RequestType.GET;
    fullUrl: string = '';
    body?: FormData | string = '';
    extraHeaders?: MapHeader;
    timeout?: number;
    isAuthenticated: boolean = false;

    constructor({
        type = RequestType.GET,
        fullUrl = '',
        body,
        extraHeaders,
        timeout = 30000,
        isAuthenticated = false
    }: {
        type?: RequestType;
        fullUrl?: string;
        body?: FormData | string;
        extraHeaders?: MapHeader;
        timeout?: number;
        isAuthenticated?: boolean;
    } = {}) {
        this.type = type;
        this.fullUrl = fullUrl;
        this.body = body;
        this.extraHeaders = extraHeaders;
        this.timeout = timeout;
        this.isAuthenticated = isAuthenticated;
    }
}

interface Error {
    message: string;
}
interface ResultType {
    errors?: Error[];
    error?: string;
    data: string | undefined | null;
    message?: string;
}

class APIResponse {
    success: boolean = false;
    statusCode: number = HTTP_CODE_SUCCESS;
    errorMessage?: string;
    date?: string;
    result: ResultType = {
        errors: [],
        error: '',
        data: '',
        message: ''
    };

    constructor({
        success = false,
        statusCode = HTTP_CODE_SUCCESS,
        result = {
            errors: [],
            error: '',
            data: '',
            message: ''
        },
        errorMessage,
        date
    }: {
        success?: boolean;
        statusCode?: number;
        result?: ResultType;
        errorMessage?: string;
        date?: string;
    } = {}) {
        this.success = success;
        this.statusCode = statusCode;
        this.result = result;
        this.errorMessage = errorMessage;
        this.date = date;
    }
}

// Standard HTTP codes
const HTTP_CODE_SUCCESS = 200;
const HTTP_CODE_SUCCESS1 = 201;
const HTTP_CODE_UNAUTHORIZED = 401;
const HTTP_CODE_NOT_FOUND = 404;
// Custom HTTP codes to indicate errors
const HTTP_CODE_BAD_RESPONSE = -1;
const HTTP_CODE_NO_INTERNET = -2;
const HTTP_CODE_UNKNOWN = -4;

type ErrorCodeName = Record<number, string>;

const errorCodeNames: ErrorCodeName = {
    [HTTP_CODE_NO_INTERNET]: 'No internet connection.',
    [HTTP_CODE_BAD_RESPONSE]: 'Something went wrong. Try again',
    [HTTP_CODE_NOT_FOUND]: 'Not found.',
    [HTTP_CODE_UNKNOWN]: 'Unknown error',
    [HTTP_CODE_UNAUTHORIZED]: 'Unauthorized'
};

class HTTPClient {
    getHeaders(request: APIRequest) {
        // Default headers
        const headers: MapHeader = {
            'Content-Type': 'application/json; charset=UTF-8'
        };

        /* eslint-disable */
        if (request.extraHeaders !== undefined) {
            Object.keys(request.extraHeaders).forEach(
                (header) => (headers[header] = request.extraHeaders![header] ?? '')
            );
        }

        if (request.isAuthenticated) {
            const authDetails = LocalStorageService.get<AuthObject>(AUTH_KEY);
            if (!authDetails) return headers;
            headers.Authorization = `Bearer ${authDetails.accessToken}`;
        }

        return headers;
    }

    async request(request: APIRequest) {
        try {
            const controller = new AbortController();
            const id = setTimeout(() => controller.abort(), request.timeout);

            let body;
            if (request.body instanceof FormData) {
                body = request.body;
            } else if (typeof request.body === 'string') {
                body = request.body;
            } else {
                body = JSON.stringify(request.body);
            }

            const headers = this.getHeaders(request);
            if (request.body instanceof FormData) {
                delete headers['Content-Type'];
            }

            /* eslint-ignore */
            const response = await fetch(`${request.fullUrl}`, {
                method: request.type,
                headers,
                body: body ?? null,
                signal: controller.signal
            });

            clearTimeout(id);

            const statusCode = response.status;

            const data = await response.json();

            if (statusCode > 400) {
                return new APIResponse({
                    success: false,
                    statusCode,
                    result: data,
                    errorMessage: errorCodeNames[HTTP_CODE_BAD_RESPONSE],
                    date: response.headers.get('date') ?? ''
                });
            }

            if (!(statusCode >= HTTP_CODE_SUCCESS && statusCode <= HTTP_CODE_SUCCESS1)) {
                return new APIResponse({
                    success: false,
                    statusCode,
                    result: data,
                    errorMessage: errorCodeNames[HTTP_CODE_BAD_RESPONSE],
                    date: response.headers.get('date') ?? ''
                });
            }

            return new APIResponse({
                success: true,
                statusCode,
                result: data,
                date: response.headers.get('date') ?? ''
            });
        } catch (_) {
            return new APIResponse({
                success: false,
                statusCode: HTTP_CODE_BAD_RESPONSE,
                errorMessage: errorCodeNames[HTTP_CODE_BAD_RESPONSE],
                result: {
                    data: '',
                    error: errorCodeNames[HTTP_CODE_BAD_RESPONSE]
                }
            });
        }
    }

    async get(request: APIRequest) {
        return await this.request(request);
    }

    async post(request: APIRequest) {
        return await this.request(request);
    }

    async patch(request: APIRequest) {
        return await this.request(request);
    }

    async put(request: APIRequest) {
        return await this.request(request);
    }

    async delete(request: APIRequest) {
        return await this.request(request);
    }
}

export {
    HTTPClient,
    APIRequest,
    APIResponse,
    RequestType,
    HTTP_CODE_UNAUTHORIZED,
    HTTP_CODE_SUCCESS
};
